import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../../../components/common/FeatherIcon';
import { getAgentTools } from '../../../services/ai-explorer/aiExplorerService';

/** A switch: a real checkbox for keyboard and screen readers, drawn as a toggle. */
const Toggle = ({ checked, onChange, disabled, label }) => (
  <span className={`sa3-toggle${checked ? ' on' : ''}${disabled ? ' disabled' : ''}`}>
    <input type="checkbox" role="switch" aria-checked={checked} aria-label={label} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    <span className="sa3-toggle-track" aria-hidden="true"><span className="sa3-toggle-knob" /></span>
  </span>
);

/**
 * The composer's Tools button and the sheet behind it: the platform tools the
 * agent may call for this user, with a master switch and per-tool switches.
 * The list comes from the API every time the sheet opens; when the tool plane
 * is unreachable the sheet says so instead of showing an empty list.
 */
export default function ToolsControl({ prefs, onChange, busy }) {
  const { t } = useTranslation('aiExplorer');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ sources: [], loading: false, error: null });
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    getAgentTools()
      .then((res) => setState({ sources: res.data?.sources || [], loading: false, error: null }))
      .catch((err) => setState({ sources: [], loading: false, error: err?.response?.data?.detail || err?.message || 'unavailable' }));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  const excluded = useMemo(() => new Set(prefs.exclude || []), [prefs.exclude]);
  const all = useMemo(() => state.sources.flatMap((s) => s.tools.map((tool) => ({ ...tool, sourceLabel: s.label }))), [state.sources]);
  const needle = q.trim().toLowerCase();
  const shown = needle ? all.filter((x) => x.name.includes(needle) || x.description.toLowerCase().includes(needle)) : all;
  const enabledCount = all.length - all.filter((x) => excluded.has(x.name)).length;

  const toggle = (name) => {
    const next = new Set(excluded);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange({ exclude: Array.from(next) });
  };

  return (
    <>
      <button type="button" className={`sa3-pill${prefs.useTools ? '' : ' off'}`} onClick={() => setOpen(true)} disabled={busy} aria-haspopup="dialog" title={prefs.useTools ? t('agent.toolsOnHint') : t('agent.toolsOffHint')}>
        <FeatherIcon name="tool" size={14} />{t('agent.tools')}
        {!prefs.useTools && <span className="sa3-pill-tag">{t('agent.off')}</span>}
      </button>
      {open && (
        <div className="sa3-scrim" role="presentation" onClick={() => setOpen(false)}>
          <div className="sa3-dialog sa3-tools" role="dialog" aria-modal="true" aria-label={t('agent.tools')} onClick={(e) => e.stopPropagation()}>
            <header>
              <span>{t('agent.tools')}</span>
              <button type="button" className="sa3-ibtn sm" onClick={() => setOpen(false)} aria-label={t('agent.close')}><FeatherIcon name="x" size={16} /></button>
            </header>
            <div className="sa3-tools-body">
              <label className="sa3-switch">
                <span><b>{t('agent.useTools')}</b><small>{t('agent.useToolsHint')}</small></span>
                <Toggle checked={!!prefs.useTools} onChange={(v) => onChange({ useTools: v })} label={t('agent.useTools')} />
              </label>
              {state.loading && <div className="sa3-state">{t('agent.loading')}</div>}
              {state.error && <div className="sa3-state error">{t('agent.toolsUnavailable')}<div style={{ fontSize: 12, marginTop: 4 }}>{String(state.error)}</div></div>}
              {!state.loading && !state.error && all.length === 0 && <div className="sa3-state">{t('agent.toolsEmpty')}</div>}
              {all.length > 0 && (
                <>
                  <div className="sa3-tools-bar">
                    <label className="sa3-search" style={{ flex: 1 }}>
                      <FeatherIcon name="search" size={14} />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('agent.toolSearch')} aria-label={t('agent.toolSearch')} />
                    </label>
                    <span className="sa3-tools-count">{t('agent.enabledCount', { count: enabledCount, total: all.length })}</span>
                  </div>
                  <ul className="sa3-tools-list">
                    {shown.map((tool) => (
                      <li key={tool.name}>
                        <label className={`sa3-tool${excluded.has(tool.name) ? ' off' : ''}${prefs.useTools ? '' : ' muted'}`}>
                          <Toggle checked={!excluded.has(tool.name)} onChange={() => toggle(tool.name)} disabled={!prefs.useTools} label={tool.name} />
                          <span className="sa3-tool-main">
                            <span className="sa3-tool-name"><code>{tool.name}</code>{tool.write && <em className="sa3-tool-write">{t('agent.needsApproval')}</em>}</span>
                            <span className="sa3-tool-desc">{tool.description}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                    {shown.length === 0 && <li className="sa3-state">{t('agent.noMatches')}</li>}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
